import { configureStore, isRejectedWithValue, Middleware, MiddlewareAPI } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { FLUSH, PAUSE, PERSIST, persistReducer, persistStore, PURGE, REGISTER, REHYDRATE } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { showToast } from '../features/toasts/toastsSlice';
import { ToastType } from '../models/toast';
import { DEFAULT_DISMISS_DURATION } from '../providers/ToastProvider';
import { assistantAPI } from '../services/assistant';
import { audiencerateApi } from '../services/audiencerate';
import { rootReducer, RootState } from './index';
import { getErrorMessageForDataSourceMutation } from '@/utils/errors';
import i18n from '../localization';

const persistConfig = {
  timeout: 0,
  key: 'audiencerate.cdp.root',
  storage,
  blacklist: ['auth', 'sidebar', 'slideOver', 'fileUploading', 'toasts', audiencerateApi.reducerPath, assistantAPI.reducerPath],
};

const errorToastMap: Record<string, number> = {};

// RTK Query reports non-HTTP failures as string statuses; HTTP failures as numbers.
const getErrorTitle = (status: unknown) => {
  switch (status) {
    case 'FETCH_ERROR':
      return i18n.t('server_errors:networkError');
    case 'TIMEOUT_ERROR':
      return i18n.t('server_errors:timeoutError');
    case 'PARSING_ERROR':
    case 'CUSTOM_ERROR':
      return i18n.t('server_errors:unexpectedError');
    default:
      return typeof status === 'number' ? `${i18n.t('ALL:error')} - ${status}` : i18n.t('ALL:error');
  }
};

const getErrorFallbackMessage = (status: unknown) =>
  typeof status === 'number' ? undefined : i18n.t('server_errors:genericErrorMessage');

const shouldShowErrorToast = (action: any) => {
  const title = getErrorTitle(action.payload?.status);
  const message = action.payload?.data?.message;

  const key = `${title}|${message}`;
  const currentTime = errorToastMap[key] || 0;
  const now = Date.now();

  if (currentTime + DEFAULT_DISMISS_DURATION > now) {
    return false;
  } else {
    errorToastMap[key] = now;
    return true;
  }
};

const IGNORED_ENDPOINTS = ['prepareConnectorFlowStep', 'inviteWorkspace', 'deleteDataSource', 'getEmailTemplates'];
export const rtkQueryErrorLogger: Middleware = (api: MiddlewareAPI) => (next) => (action) => {
  // RTK Query uses `createAsyncThunk` from redux-toolkit under the hood, so we're able to utilize these use matchers!
  if (isRejectedWithValue(action)) {
    const endpointName = action.meta?.arg?.endpointName;
    if (IGNORED_ENDPOINTS.includes(endpointName)) {
      return next(action);
    }
    console.log('action', action);
    const { type } = action;
    if (type === 'audiencerateApi/executeMutation/rejected') {
      const errorMessage = getErrorMessageForDataSourceMutation({
        responseMessage: action.payload?.data?.message,
        codes: action.payload?.data?.content || [],
      });
      if (shouldShowErrorToast(action)) {
        api.dispatch(
          showToast({
            type: ToastType.ERROR,
            title: getErrorTitle(action.payload?.status),
            message: errorMessage || getErrorFallbackMessage(action.payload?.status),
          })
        );
      }
    } else if (shouldShowErrorToast(action)) {
      api.dispatch(
        showToast({
          type: ToastType.ERROR,
          title: getErrorTitle(action.payload?.status),
          message: action.payload?.data?.message || getErrorFallbackMessage(action.payload?.status),
        })
      );
    }
  }

  return next(action);
};

export function configureAppStore(preloadedState: any) {
  const reducer = persistReducer<RootState>(persistConfig, rootReducer);

  const store = configureStore({
    reducer,
    // put here RTK api with .concat on gdm
    middleware: (gdm) =>
      gdm({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
      })
        .concat(rtkQueryErrorLogger)
        .concat(audiencerateApi.middleware)
        .concat(assistantAPI.middleware),
    preloadedState,
  });
  const persistor = persistStore(store);

  setupListeners(store.dispatch);

  return { store, persistor };
}
